import { useMemo } from "react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { CalendarDays } from "lucide-react";
import { fyToRange } from "@/components/FYPicker";

const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/**
 * Compact month filter. `value` is "" (all) or "YYYY-MM".
 * When `fy` is provided, options are scoped to that FY (Apr–Mar);
 * otherwise the last 24 calendar months are offered.
 */
export default function MonthPicker({ value = "", onChange, fy = "all", size = "sm" }) {
  const options = useMemo(() => {
    const opts = [];
    if (fy && fy !== "all") {
      const startYear = parseInt(fy, 10);
      // FY runs Apr (startYear) → Mar (startYear+1)
      for (let i = 0; i < 12; i++) {
        const m = (4 + i - 1) % 12;            // 3=Apr ... 2=Mar
        const y = startYear + Math.floor((4 + i - 1) / 12);
        opts.push({ key: `${y}-${String(m + 1).padStart(2, "0")}`, label: `${MONTH_NAMES[m]} ${y}` });
      }
      return opts;
    }
    // last 24 months
    const now = new Date();
    for (let i = 0; i < 24; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      opts.push({
        key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`,
        label: `${MONTH_NAMES[d.getMonth()]} ${d.getFullYear()}`,
      });
    }
    return opts;
  }, [fy]);

  return (
    <Select value={value || "all"} onValueChange={(v) => onChange(v === "all" ? "" : v)}>
      <SelectTrigger
        className={`${size === "sm" ? "h-9 text-[12px]" : "h-10"} w-[140px] gap-1.5`}
        data-testid="month-picker"
      >
        <CalendarDays className="h-3.5 w-3.5 text-slate-400 shrink-0" />
        <SelectValue placeholder="All months" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all">All months</SelectItem>
        {options.map((o) => (
          <SelectItem key={o.key} value={o.key}>{o.label}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/** "YYYY-MM" → {from: "YYYY-MM-01", to: "YYYY-MM-<last>"} or nulls. */
export function monthToRange(month) {
  if (!month) return { from: null, to: null };
  const [y, m] = month.split("-").map(Number);
  const last = new Date(y, m, 0).getDate(); // day 0 of next month = last day of this month
  return {
    from: `${y}-${String(m).padStart(2, "0")}-01`,
    to: `${y}-${String(m).padStart(2, "0")}-${String(last).padStart(2, "0")}`,
  };
}
